import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Grid } from "@mui/material";
import {
  Alert,
  Button,
  SectionNavigation,
  SectionNavigationItem,
  Typography,
} from "@platform/design-system";
import { useRouteMatch } from "@platform/hooks";
import { BrokerPartnerUsersResponse } from "@platform/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router";
import { useApi } from "@platform/api";

import { Loader } from "../../components";
import { ROUTE_NAMES } from "../../constants";
import { extractErrorMessage } from "../../utils";
import InviteDialog from "../ManageUsers/inviteDialog";

import {
  BrokerPartner as BrokerPartnerType,
  CommonContext,
  ConnectedCarriersContext,
  RfpsContext,
  UsersContext,
} from "./types";

const BrokerPartner = (): React.JSX.Element => {
  const [statusError, setStatusError] = useState<string | null>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const route = useRouteMatch(ROUTE_PATHS, {
    baseRoute: `/${ROUTE_NAMES.BROKERS}/:id/`,
  });
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const axiosInstance = useApi();

  const brokerId = params.id;
  const brokerIdInt = Number(brokerId);
  const brokerQuery = useQuery<BrokerPartnerType>({
    queryKey: ["prepareManageBrokers", brokerId],
    queryFn: () =>
      axiosInstance
        .get<BrokerPartnerType>(
          `/brokerportal/v1/prepareManageBrokers/${brokerId}`,
        )
        .then((res) => res.data),
  });

  const queryKey = ["brokerPartnerUsers", brokerId];
  const usersQuery = useQuery<BrokerPartnerUsersResponse>({
    queryKey,
    queryFn: () =>
      axiosInstance
        .post<BrokerPartnerUsersResponse>(
          "/brokerportal/v1/brokerPartnerUsers",
          {
            brokerId: brokerIdInt,
          },
        )
        .then((res) => res.data),
    enabled: !(brokerQuery.isLoading || brokerQuery.isError),
  });

  const queryClient = useQueryClient();
  const updateQueryData = <T extends keyof BrokerPartnerUsersResponse>(
    field: T,
    newData: BrokerPartnerUsersResponse[T],
  ): void => {
    queryClient.setQueryData(
      queryKey,
      (oldData?: BrokerPartnerUsersResponse) =>
        oldData
          ? {
              ...oldData,
              [field]: newData,
            }
          : oldData,
    );
  };

  const isLoading = brokerQuery.isLoading || usersQuery.isLoading;
  if (isLoading) {
    return <Loader />;
  }

  const isError = brokerQuery.isError || usersQuery.isError;
  if (isError) {
    return (
      <Box mt={2} mb={2}>
        <Alert severity="error">
          {extractErrorMessage(brokerQuery.error || usersQuery.error)}
        </Alert>
      </Box>
    );
  }

  const { name: brokerName } = brokerQuery.data;

  const context: {
    users: UsersContext;
    clients: CommonContext;
    draftRfps: RfpsContext;
    submittedRfps: RfpsContext;
    connectedCarriers: ConnectedCarriersContext;
    configuration: CommonContext;
  } = {
    users: {
      data: usersQuery.data,
      brokerId: brokerIdInt,
      queryKey,
      updateQueryData,
      setStatusError,
    },
    clients: { brokerId: brokerIdInt },
    draftRfps: { brokerId: brokerIdInt },
    submittedRfps: { brokerId: brokerIdInt },
    connectedCarriers: {
      brokerId: brokerIdInt,
      brokerName,
    },
    configuration: { brokerId: brokerIdInt },
  };

  const handleCloseInviteDialog = (): void => {
    setShowInviteDialog(false);
  };

  return (
    <Box>
      {statusError && (
        <Box mt={2} mb={2}>
          <Alert severity="error">{statusError}</Alert>
        </Box>
      )}
      <Box mb={3} textAlign="left">
        <Button
          startIcon={<ArrowBackIcon />}
          variant="tertiary"
          onClick={() => navigate(`/${ROUTE_NAMES.BROKERS}`)}
        >
          Back
        </Button>
      </Box>
      <Box
        mb={5}
        sx={{
          width: "100%",
          justifyContent: "space-between",
          display: "flex",
        }}
      >
        <Typography variant="display-medium">{brokerName}</Typography>
        {route === "users" ? (
          <Box
            sx={{
              whiteSpace: "nowrap",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              variant="primary"
              onClick={() => setShowInviteDialog(true)}
              startIcon={<AddIcon />}
            >
              Add user
            </Button>
          </Box>
        ) : null}
      </Box>
      <Grid
        size={12}
        sx={{ borderBottom: 1, borderColor: "divider" }}
        mt={3}
        mb={5}
      >
        <SectionNavigation navItems={NAV_ITEMS} value={route} />
      </Grid>
      <Box width="100%" mt={5}>
        <Outlet context={context[route]} />
      </Box>

      <InviteDialog
        open={showInviteDialog}
        onClose={handleCloseInviteDialog}
        brokerId={brokerIdInt}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey });
          handleCloseInviteDialog();
        }}
      />
    </Box>
  );
};

type RouteKey =
  | "users"
  | "clients"
  | "submittedRfps"
  | "draftRfps"
  | "connectedCarriers"
  | "configuration";

const NAV_ITEMS: SectionNavigationItem<RouteKey>[] = [
  {
    label: "Users",
    path: "",
    value: "users",
  },
  {
    label: "Clients",
    path: ROUTE_NAMES.CLIENTS,
    value: "clients",
  },
  {
    label: "Submitted RFPs",
    path: ROUTE_NAMES.BROKER_PARTNER_SUBMITTED_RFPS,
    value: "submittedRfps",
  },
  {
    label: "Draft RFPs",
    path: ROUTE_NAMES.BROKER_PARTNER_DRAFT_RFPS,
    value: "draftRfps",
  },
  {
    label: "Connected Carriers",
    path: ROUTE_NAMES.BROKER_PARTNER_CONNECTED_CARRIERS,
    value: "connectedCarriers",
  },
  {
    label: "Configuration",
    path: ROUTE_NAMES.BROKER_PARTNER_CONFIGURATION,
    value: "configuration",
  },
];

const ROUTE_PATHS = NAV_ITEMS.map<[RouteKey, string]>((item) => [
  item.value,
  item.path,
]);

export { BrokerPartner };
